import type { LinkProps } from '@tanstack/react-router'
import {
  Activity,
  Building2,
  CalendarClock,
  ClipboardCheck,
  Layers,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react'

import { HEALTH_CHECK_KEYS, type HealthReport, type HealthStatus } from '@/schemas/portfolio'

/**
 * Status vocabulary shared by the Overview rail's health block
 * (`OverviewRail.tsx`) and the full `/portfolio/health` screen — one copy, so
 * a colour or a word never drifts between the two places a member sees it.
 *
 * Borrowed from the documented STATUS trio (`--success`/`--warning-fill`/
 * `--destructive`) rather than a dedicated non-directional-ramp token: the
 * system has no Layer-2 token for a three-step steady/watch/act scale yet.
 */
export const HEALTH_STATUS: Record<HealthStatus, { label: string; dot: string; bar: string }> = {
  steady: { label: 'Steady', dot: 'bg-success', bar: 'bg-success' },
  watch: { label: 'Watch', dot: 'bg-warning-fill', bar: 'bg-warning-fill' },
  act: { label: 'Look at this', dot: 'bg-destructive', bar: 'bg-destructive' },
}

/** Worst-first — the order every design frame groups checks in. */
export const HEALTH_STATUS_ORDER: readonly HealthStatus[] = ['act', 'watch', 'steady']

/** The order the summary strip and count tiles draw in: best-first. */
export const HEALTH_STRIP_ORDER: readonly HealthStatus[] = ['steady', 'watch', 'act']

/**
 * "4 of 6 checks look steady" — built from the payload's own length, never a
 * hardcoded count, so a check added upstream is picked up with no change here.
 */
export const healthHeadline = (report: HealthReport): string =>
  `${String(report.steady)} of ${String(report.checks.length)} check${
    report.checks.length === 1 ? '' : 's'
  } look${report.steady === 1 ? 's' : ''} steady`

/**
 * The one check Free shows in full (`portfolio.md` §11 Free-paywall render:
 * "Free shows record quality. Sadasya opens the other six checks…"). Every
 * other key is paywalled for a `free`-plan member — Health itself is not
 * tier-gated on the backend, so this split is presentation only.
 */
export const HEALTH_FREE_CHECK_KEY: string = 'record_quality'

/**
 * Icon per check key. The design draws a bespoke illustrated glyph per check
 * that `src/assets/` has no equivalent for, so a plain Lucide glyph in a
 * neutral `IconTile` stands in — the same "real icon, not invented art"
 * choice `GatedCell` already makes for a missing logo.
 */
export const HEALTH_CHECK_ICON: Record<string, LucideIcon> = {
  record_quality: ClipboardCheck,
  company_spread: Building2,
  sector_spread: Layers,
  below_cost: TrendingDown,
  upcoming_dates: CalendarClock,
  trading_pace: Activity,
}

/**
 * Where a check's own go-to-link (session 5095, every check card) leads — the
 * existing tab that already shows the data behind that check, so the arrow is
 * a real shortcut rather than a link to nowhere.
 */
export const HEALTH_CHECK_LINK: Record<
  string,
  { to: NonNullable<LinkProps['to']>; label: string }
> = {
  record_quality: { to: '/portfolio/activity', label: 'Review transactions' },
  company_spread: { to: '/portfolio/allocation', label: 'See company allocation' },
  sector_spread: { to: '/portfolio/allocation', label: 'See sector allocation' },
  below_cost: { to: '/portfolio/holdings', label: 'See holdings below cost' },
  upcoming_dates: { to: '/portfolio/income', label: 'See upcoming dates' },
  trading_pace: { to: '/portfolio/activity', label: 'See activity' },
}

/**
 * Title + one-line description per key, for the ONE place there is no
 * `HealthCheck` to read a real `label`/`detail` off: the empty ("no profile")
 * page's "What it looks at" preview. Titles match what the API's own `label`
 * already reads wherever a real report exists; nowhere else in this family
 * hardcodes a check's name — every other render uses `check.label` verbatim.
 */
export const HEALTH_CHECK_PREVIEW: Record<string, { title: string; description: string }> = {
  record_quality: {
    title: 'Record quality',
    description: 'Every trade needs a price. This flags the ones that are missing one.',
  },
  company_spread: {
    title: 'Company spread',
    description: 'How much of the book sits in one company.',
  },
  sector_spread: {
    title: 'Sector spread',
    description: 'How much of the book sits in one sector.',
  },
  below_cost: {
    title: 'Holdings below cost',
    description: 'Shares now worth less than what you paid for them.',
  },
  upcoming_dates: {
    title: 'Dates coming up',
    description: 'Book closures and other dates on the companies you hold.',
  },
  trading_pace: {
    title: 'Trading pace',
    description: 'How often you buy and sell, compared with holding.',
  },
}

/** `HEALTH_CHECK_KEYS`, re-exported from here so a health component needs one import. */
export { HEALTH_CHECK_KEYS }
